import * as React from 'react'
import {ReactElement} from 'react'
import {Nullable} from '../../../../app/TypeHelpers'
import {Box} from '@mui/material'
import {Colors, FontSize, LineHeight} from '../../../../styledComponents/Constants'
import {
    BoldText,
    ListGridContainer,
    NumberListContainer,
    NumberListItem,
    PageTitle,
    PolicyCheckBoxStyled,
} from '../../applications/common/resident/NatResidentApplicationsCommon'
import {BlockSpacer, currentMedia} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {PlainLink} from '../../../../styledComponents/Links'
import {FullWidthColumnFlexContainer, LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {ROLE_FOR_GUEST} from '../../../../CodeMaster'
import styled from 'styled-components'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import {GuestApplyButton, GuestBackButton, GuestPlainLinkStyledBackButton} from '../common/NatOwnershipTransfersCommon'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import NatResidentStaticContentsComponent from '../../common/resident/NatResidentStaticContentsComponent'
import {StyledDiv} from '../../../../styledComponents/StyledTypeDefinitions'
import {Location} from 'react-router-dom'

interface OwnerShipLocationState {
    applyId: string
}

interface Props {
    condominiumCd: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    roleId: ROLE_FOR_GUEST
    location: Location<OwnerShipLocationState>
}

interface State {
    termOfUseChecked: boolean
    personalInfoProtectChecked: boolean
    policyChecked: boolean
    applyButtonDisabled: boolean
}

class NatOwnershipTransfersAgreementPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            termOfUseChecked: false,
            personalInfoProtectChecked: false,
            policyChecked: false,
            applyButtonDisabled: true,
        }

        this.onChangeTermOfUseCheckEvent = this.onChangeTermOfUseCheckEvent.bind(this)
        this.onChangePersonalInfoProtectCheckEvent = this.onChangePersonalInfoProtectCheckEvent.bind(this)
        this.onChangePolicyChecked = this.onChangePolicyChecked.bind(this)
        this.updateApplyButtonDisabled = this.updateApplyButtonDisabled.bind(this)
        this.onClickBackButton = this.onClickBackButton.bind(this)
        this.onClickApplyButton = this.onClickApplyButton.bind(this)
    }

    // 利用規約に同意するにチェックした場合
    onChangeTermOfUseCheckEvent(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            termOfUseChecked: event.target.checked,
        }, () => {
            this.updateApplyButtonDisabled()
        })
    }

    // 個人情報保護方針に同意するにチェックした場合
    onChangePersonalInfoProtectCheckEvent(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            personalInfoProtectChecked: event.target.checked,
        }, () => {
            this.updateApplyButtonDisabled()
        })
    }

    // 個人情報保護方針のリンク
    privacyWebURLLinkClick(): void {
        window.open('https://www.haseko-hcm.co.jp/privacy/', '_blank')
    };

    // 誓約文に同意するにチェックした場合
    onChangePolicyChecked(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            policyChecked: event.target.checked,
        }, () => {
            this.updateApplyButtonDisabled()
        })
    }

    updateApplyButtonDisabled(): void {
        const {termOfUseChecked, personalInfoProtectChecked, policyChecked} = this.state

        let result: boolean = !termOfUseChecked || !personalInfoProtectChecked
        if (this.props.roleId === ROLE_FOR_GUEST.NEW_MEMBER) {
            result ||= !policyChecked
        }
        this.setState({applyButtonDisabled: result})
    }

    async onClickBackButton(): Promise<void> {
        const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            const accessKeyByLoginUser = await this.props.asyncCallFunction(
                ownershipTransfersRepo.getAccessKeyByLoginUser.bind(ownershipTransfersRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            const accessKey = accessKeyByLoginUser?.details?.accessKey ?? ''
            this.props.navigate(`/guest/mypage?accessKey=${accessKey}`, {replace: true})
        } finally {
            eventEmitter.closeLoading()
        }
    }

    onClickApplyButton(): void {
        let path
        switch (this.props.roleId) {
            case ROLE_FOR_GUEST.INTERMEDIARY:
                path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/input`
                break
            case ROLE_FOR_GUEST.OLD_MEMBER:
                path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/paymentChangeDateConfirm`
                break
            default:
                path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/paymentChangeDateConfirm`
                break
        }
        this.props.navigate(
            path,
            {
                replace: true,
                state: {applyId: this.props.location.state?.applyId},
            },
        )
    }

    renderDescription(): Nullable<ReactElement> {
        return (
            <>
                <DescriptionBlock>
                    住戸の区分所有者の変更がある場合に必ず提出が必要です。
                    <Box height={'10px'}/>
                    <ListGridContainer fontSize={FontSize.Middle}>
                        <div>・</div>
                        <div>区分所有権移転日が組合員資格変更日となります。</div>
                        <div>・</div>
                        <div>組合員資格変更日が確定した後で、入力を開始してください。</div>
                        <div>・</div>
                        <div>組合員資格変更日から、管理費等の支払期日がルールに基づき算出されます。</div>
                    </ListGridContainer>
                    {(this.props.roleId === ROLE_FOR_GUEST.INTERMEDIARY) &&
                        <>
                            <Box>
                                <ListGridContainer fontSize={FontSize.Middle}>
                                    <div>・</div>
                                    <div>提出後、新旧組合員に「組合員資格変更届のご案内」を配布し、決済の場での提出を依頼してください。</div>
                                </ListGridContainer>
                            </Box>
                        </>
                    }
                </DescriptionBlock>
            </>
        )
    }

    renderTerms(): Nullable<ReactElement> {
        return (
            <Box>
                <PolicyBlock>
                    <NatResidentStaticContentsComponent contents={`/resources/contents/policy/termsGuest.html`}/>
                </PolicyBlock>
                <Box height={'20px'}/>
                <FullWidthColumnFlexContainer>
                    <PolicyCheckBoxStyled>
                        <LeftRowFlexContainer>
                            <GuestInput
                                type="checkbox"
                                checked={this.state.termOfUseChecked}
                                onChange={this.onChangeTermOfUseCheckEvent}
                            />
                            <BlockSpacer width={'10px'}/>
                            利用規約に同意する
                        </LeftRowFlexContainer>
                    </PolicyCheckBoxStyled>
                </FullWidthColumnFlexContainer>
            </Box>
        )
    }

    renderPersonalInfoProtect(): Nullable<ReactElement> {
        return (
            <Box>
                <PledgeArea fontSize={FontSize.Middle}>
                    <Box p={'10px'}>
                        <GuestBoldText>個人情報保護方針</GuestBoldText>
                        <Box height={'5px'}/>
                        <SmallPlainLink onClick={this.privacyWebURLLinkClick} type={'button'}>
                            個人情報保護方針
                        </SmallPlainLink>
                        をご確認ください。
                    </Box>
                </PledgeArea>
                <Box height={'20px'}/>
                <FullWidthColumnFlexContainer>
                    <PolicyCheckBoxStyled>
                        <LeftRowFlexContainer>
                            <GuestInput
                                type="checkbox"
                                checked={this.state.personalInfoProtectChecked}
                                onChange={this.onChangePersonalInfoProtectCheckEvent}
                            />
                            <BlockSpacer width={'10px'}/>
                            個人情報保護方針に同意する
                        </LeftRowFlexContainer>
                    </PolicyCheckBoxStyled>
                </FullWidthColumnFlexContainer>
            </Box>
        )
    }

    renderPledge(): Nullable<ReactElement> {
        if (this.props.roleId !== ROLE_FOR_GUEST.NEW_MEMBER) {
            return (<></>)
        }

        const policyList: string[] = []
        policyList.push('組合員資格の変更について届出をいたします。')
        policyList.push('新組合員は、規約（反社会的勢力の排除）に基づき、自らが反社会的勢力でないことを表明し、その後も反社会勢力にならないことを確約いたします。')
        policyList.push('新組合員は、規約（管理費等の徴収）規定により、管理組合の指定する金融機関に管理費等の支払口座を開設し、預金口座の振替手続きを行います。')

        return (
            <>
                <Box>
                    <PledgeArea fontSize={FontSize.Middle}>
                        <Box p={'10px'}>
                            <GuestBoldText>誓約文</GuestBoldText>
                            <Box height={'5px'}/>
                            <NumberListContainer>
                                {policyList.map((policy, index) => {
                                    return (<NumberListItem key={index}>{policy}</NumberListItem>)
                                })}
                            </NumberListContainer>
                        </Box>
                    </PledgeArea>
                    <Box height={'20px'}/>
                </Box>
                <FullWidthColumnFlexContainer>
                    <PolicyCheckBoxStyled>
                        <LeftRowFlexContainer>
                            <GuestInput
                                type="checkbox"
                                checked={this.state.policyChecked}
                                onChange={this.onChangePolicyChecked}
                            />
                            <BlockSpacer width={'10px'}/>
                            誓約文に同意する
                        </LeftRowFlexContainer>
                    </PolicyCheckBoxStyled>
                </FullWidthColumnFlexContainer>
                <Box height={'30px'}/>
            </>
        )
    }

    render(): ReactElement {
        return (
            <>
                <GuestPlainLinkStyledBackButton
                    onClick={this.onClickBackButton}>
                    戻る
                </GuestPlainLinkStyledBackButton>
                <BlockSpacer height={'14px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <CustomPageTitle>
                    組合員資格変更届
                </CustomPageTitle>
                <Box height={'20px'}/>
                {this.renderDescription()}
                <Box height={'20px'}/>
                {this.renderTerms()}
                <Box height={'30px'}/>
                {this.renderPersonalInfoProtect()}
                <Box height={'30px'}/>
                {this.renderPledge()}
                <GuestApplyButton
                    disabled={this.state.applyButtonDisabled}
                    onClick={this.onClickApplyButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            次へ進む
                        </div>
                    </FullWidthColumnFlexContainer>
                </GuestApplyButton>
                <Box height={'30px'}/>
                <GuestBackButton
                    onClick={this.onClickBackButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            戻る
                        </div>
                    </FullWidthColumnFlexContainer>
                </GuestBackButton>
                <Box height={'15px'}/>
            </>
        )
    }
}

export const PledgeArea = styled.div<{ fontSize?: string }>`
  background-color: ${Colors.Surface};
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid ${Colors.AccentLight};
  ${props => props.fontSize ? `
    font-size:${props.fontSize};
  ` : ''}
`

const DescriptionBlock: StyledDiv = styled.div`
  padding-left: 15px;
  ${currentMedia.lessThan('sp')`
      padding-left: 0;
  `};
`

const PolicyBlock: StyledDiv = styled.div`
  height: 400px;
  padding: 32px;
  border: 1px solid ${Colors.AccentLight};
  background-color: ${Colors.Surface};
  overflow-y: scroll;
`

export const SmallPlainLink = styled(PlainLink)`
  display: inline-block;
  font-size: ${FontSize.Middle};
  line-height: ${LineHeight.Basic};
  text-align: left;
  color: ${Colors.GuestPrimary};
  margin: 0;
`

const GuestInput = styled('input')`
  accent-color: ${Colors.GuestPrimary};
`

export const CustomPageTitle = styled(PageTitle)`
  padding-left: 15px;
  ${currentMedia.lessThan('sp')`
    padding-left: 0;
  `};
`

export const GuestBoldText = styled(BoldText)`
  font-size: 16px;
  color: ${Colors.Body};
`

export default createGuestWithErrorHandlingPage(NatOwnershipTransfersAgreementPage)
